/**
 * Support Resolver — MeesterJay 4.1
 *
 * §1.2B STABILIZATION: Unified resolution with priority logic.
 *
 * Two resolution paths:
 * 1. `resolveSupportContent(question)` — Legacy path used by PracticeSession/FeedbackDisplay.
 *    Resolves from question.supportAids/feedback → family registry → fallback.
 * 2. `resolveUnifiedSupport(familyId, seedSupport?)` — Canonical path (§1.2B).
 *    Merges seed-level and family-level support with clear priority: seed > family.
 *
 * Naming convention (§1.2C):
 *   familySupportRegistry.defaultExplanation → types.ts howItWorks → resolved explanation.body
 *   familySupportRegistry.hints[]            → resolved hints[]
 *   seed.afterSummary                        → resolved afterSummary
 *   familySupportRegistry.cheatSheet         → resolved cheatSheet (family-only, seeds don't define)
 *
 */
import { Question } from '../content'
import { VisualAid } from '../visuals'
import { HintStep } from './hints'
import {
  FAMILY_SUPPORT_REGISTRY,
  FamilySupportEntry,
  getFamilySupportEntry,
} from './familySupportRegistry'
import type {
  ResolvedSupport,
  ExplanationContent,
  CheatSheetContent,
  SupportCoverage,
  SeedSupportDefinition,
  FamilySupportDefinition,
} from './types'
import { calculateCoverage as calcCoverage } from './types'

// ─── Question-level resolved support ─────────────────────────────────────────
// Used by `resolveSupportContent()` — the per-question resolution path.
// For the canonical family-level resolution, use `resolveUnifiedSupport()` → `ResolvedSupport`.

export interface QuestionResolvedSupport {
  hints: HintStep[]
  visual?: VisualAid
  explanation?: string
  source: 'question' | 'family' | 'representation' | 'none'
  solutionSteps?: string[]
}

/**
 * Deterministically resolve support content for a question.
 *
 * Resolution Order:
 * 1. Question-level overrides (if provided in seed)
 * 2. Representation-specific support (if representationId matches)
 * 3. Family-level default support
 * 4. Fallback (empty)
 *
 * STAGE 3: Enhanced to support new feedback structure
 *
 * @returns QuestionResolvedSupport — the per-question format used by FeedbackDisplay/ExplanationCard
 */
export function resolveSupportContent(
  question: Question,
): QuestionResolvedSupport {
  const familyId = question.familyId
  const registryEntry = FAMILY_SUPPORT_REGISTRY[familyId]

  // Check for representationId in question data
  const representationId = question.data?.representationId as string | undefined
  const representationSupport =
    representationId && registryEntry?.representations?.[representationId]

  // Resolve Hints
  let hints: HintStep[] = []
  if (representationSupport?.hints) {
    hints = representationSupport.hints
  } else if (registryEntry?.hints) {
    hints = registryEntry.hints
  }

  // STAGE 3: Resolve Visual from canonical supportAids + family fallbacks
  let visual: VisualAid | undefined

  // Check new supportAids structure first
  if (question.supportAids && question.supportAids.length > 0) {
    visual = question.supportAids[0] // Use first support visual
  } else {
    // Family/representation fallback
    if (representationSupport?.visual) {
      visual = representationSupport.visual
    } else if (registryEntry?.defaultVisual) {
      visual = registryEntry.defaultVisual
    }
  }

  // Resolve explanation from canonical question.feedback + registry fallback
  let explanation: string | undefined
  let solutionSteps: string[] | undefined
  if (question.feedback) {
    explanation = question.feedback.explanationText
    solutionSteps = question.feedback.solutionSteps
  } else {
    // Family/representation fallback
    if (representationSupport?.explanation) {
      explanation = representationSupport.explanation
    } else if (registryEntry?.defaultExplanation) {
      explanation = registryEntry.defaultExplanation
    }
  }

  // Determine source for debugging/reporting
  let source: QuestionResolvedSupport['source'] = 'none'
  if (question.supportAids || question.feedback) {
    source = 'question'
  } else if (representationSupport) {
    source = 'representation'
  } else if (registryEntry) {
    source = 'family'
  }
  return {
    hints,
    visual,
    explanation,
    solutionSteps,
    source,
  }
}

// ─── §1.2B: Bridge function ─────────────────────────────────────────────────
// Maps FamilySupportEntry (registry format) → FamilySupportDefinition (types.ts format)
// This bridges the naming gap: defaultExplanation → howItWorks (§1.2C)

function getFamilySupport(familyId: string): FamilySupportDefinition | null {
  const entry = getFamilySupportEntry(familyId)
  if (!entry) return null

  return {
    familyId: entry.familyId,
    howItWorks: entry.defaultExplanation, // §1.2C: defaultExplanation → howItWorks
    hints: entry.hints?.map((h) => h.content || h.title || '') || [],
    steps: undefined, // FamilySupportEntry doesn't have steps (seed-only)
    cheatSheet: undefined, // Populated separately below if available
    afterSummary: undefined, // Family-level afterSummary not in current registry
  }
}

/**
 * Unified support resolution (§1.2B).
 *
 * Priority: seed-level > family-level (seed is more specific).
 * Returns a fully resolved ResolvedSupport object with coverage diagnostics.
 *
 * @param familyId - The canonical family ID
 * @param seedSupport - Optional seed-level support overrides
 * @returns ResolvedSupport from types.ts (canonical format)
 */
export function resolveUnifiedSupport(
  familyId: string,
  seedSupport?: SeedSupportDefinition,
): ResolvedSupport {
  const familyDef = getFamilySupport(familyId)
  const registryEntry = getFamilySupportEntry(familyId)
  const sources: ('family' | 'seed')[] = []

  // --- Explanation (seed.howItWorks > family.howItWorks) ---
  let explanation: ExplanationContent | null = null
  const explanationText = seedSupport?.howItWorks || familyDef?.howItWorks
  if (explanationText) {
    const source: 'seed' | 'family' = seedSupport?.howItWorks
      ? 'seed'
      : 'family'
    explanation = {
      title: 'Zo werkt het',
      body: explanationText,
      steps: seedSupport?.steps || familyDef?.steps,
      hints: seedSupport?.hints || familyDef?.hints,
      source,
    }
    sources.push(source)
  }

  // --- Steps (seed > family) ---
  const steps = seedSupport?.steps || familyDef?.steps || []

  // --- Hints (seed > family) ---
  const hints = seedSupport?.hints || familyDef?.hints || []

  // --- Cheat sheet (family registry only — seeds don't define cheat sheets) ---
  let cheatSheet: CheatSheetContent | null = null
  // Check the raw registry entry for cheatSheet-like content
  // Currently, cheat sheets are derived from the family's hints + explanation
  // Future: add explicit cheatSheet field to FamilySupportEntry

  // --- After summary (seed > family) ---
  const afterSummary =
    seedSupport?.afterSummary || familyDef?.afterSummary || null

  // --- Coverage ---
  const coverageBase = {
    hasExplanation: !!explanation,
    hasCheatSheet: !!cheatSheet,
    hasHints: hints.length > 0,
    hasSteps: steps.length > 0,
    hasAfterSummary: !!afterSummary,
  }

  const coverage: SupportCoverage = {
    ...coverageBase,
    completeness: calcCoverage(coverageBase),
    sources,
  }

  return {
    explanation,
    cheatSheet,
    afterSummary,
    hints,
    steps,
    coverage,
  }
}
